import { Property, PropertyViewModel, Value, Values } from "@/Models/Eav";
import { TreeViewCacheItem } from "@/Models/Treeview";
import { Viewer } from "@xeokit/xeokit-sdk";
import { useMemo } from "react";

const useSelectionProperties = ({
    selectedEntities,
    viewer,
    treeViewCache,
    objects,
    search,
    t,
}: {
    selectedEntities: string[];
    viewer: Viewer | null;
    treeViewCache: TreeViewCacheItem[];
    objects: PropertyViewModel[];
    search: string;
    t: (key: string) => string;
}) => {
    return useMemo(() => {
        if (selectedEntities.length >= 10) {
            return {
                properties: [] as Property[],
                errorMessage: t("propWinErrorTooManySelected"),
            };
        }

        const debouncedQuery = search.toLowerCase().trim();
        const selectedModels = new Set<string>();
        for (const sel of selectedEntities) {
            if (sel.includes("#")) {
                selectedModels.add(sel.split("#")[0]);
            } else if (viewer) {
                viewer.scene.objectIds
                    .filter((o: string) => o.includes(sel))
                    .forEach((o: string) => selectedModels.add(o.split("#")[0]));
            }
        }
        const multiModelSelection = selectedModels.size > 1;

        type RawProperty = { category: string; values: Values[] };

        const getPropertiesForElement = (model: PropertyViewModel, externalId: string): RawProperty[] => {
            const { indices } = model;
            const entityId = indices.entityIdByExternalId.get(externalId);
            if (!entityId) return [];

            const eavRows = indices.eavByEntityId.get(entityId) ?? [];
            const idLabel = multiModelSelection ? `${model.modelId}[${externalId}]` : `${externalId}`;

            const baseValues: Values[] = eavRows
                .map((row) => {
                    const attr = indices.attrsById.get(row.AttributeId);
                    if (!attr) return undefined;

                    const val = indices.valuesById.get(row.ValueId)?.Value ?? "";
                    return {
                        attr,
                        id: idLabel,
                        value: val,
                    };
                })
                .filter(Boolean) as Values[];

            const classifications = indices.classificationsByEntityId.get(entityId) ?? [];
            const materials = indices.materialsByEntityId.get(entityId) ?? [];
            const grouped: RawProperty[] = [];

            // Category groups
            const categories = new Set(baseValues.map((v) => v.attr.Category));
            for (const category of categories) {
                grouped.push({
                    category,
                    values: baseValues.filter((v) => v.attr.Category === category),
                });
            }

            // Classifications
            if (classifications.length) {
                grouped.push({
                    category: t("propWinClassifications"),
                    values: classifications.map((c) => ({
                        attr: {
                            Id: c.Id,
                            Name: c.Name,
                            Category: c.ClassificationSystem,
                            DataType: "STRING",
                        },
                        id: idLabel,
                        value: `${t("propWinLocation")}:${c.Location}\n${t("propWinReference")}:${c.Reference}`,
                    })),
                });
            }

            // Materials
            if (materials.length) {
                grouped.push({
                    category: t("propWinMaterials"),
                    values: materials.map((m) => ({
                        attr: {
                            Id: m.Id,
                            Name: m.Name,
                            Category: "Material",
                            DataType: "STRING",
                        },
                        id: idLabel,
                        value: "-",
                    })),
                });
            }

            return grouped;
        };

        let collected: RawProperty[] = [];
        for (const sel of selectedEntities) {
            const ids = sel.includes("#")
                ? [sel]
                : (viewer?.scene.objectIds.filter((o: string) => o.includes(sel)) ?? []);

            for (const full of ids) {
                if (!full.includes("#")) continue;

                const [modelId, elementId] = full.split("#");
                const model = objects.find((m) => m.modelId === modelId);
                if (!model) continue;

                const props = getPropertiesForElement(model, elementId);
                collected = collected.concat(props);
            }
        }

        if (debouncedQuery) {
            collected = collected
                .map((grp) => {
                    const filtered = grp.values.filter((v) => {
                        const value = String(v.value ?? "").toLowerCase();
                        const attr = v.attr.Name.toLowerCase();
                        const cat = grp.category.toLowerCase();

                        return (
                            value.includes(debouncedQuery) ||
                            attr.includes(debouncedQuery) ||
                            cat.includes(debouncedQuery)
                        );
                    });

                    return filtered.length ? { ...grp, values: filtered } : null;
                })
                .filter((x): x is RawProperty => x !== null);
        }

        const allCategories = Array.from(new Set(collected.map((p) => p.category)));
        const grouped: Property[] = [];

        for (const category of allCategories) {
            const values = collected.filter((p) => p.category === category).flatMap((p) => p.values);

            const attrNames = new Set(values.map((v) => v.attr.Name));
            const finalValues: Value[] = [];

            for (const attr of attrNames) {
                const rel = values.filter((v) => v.attr.Name === attr);

                // Unique by value
                const unique = Array.from(new Map(rel.map((x) => [x.value, x])).values());

                if (unique.length > 1) {
                    finalValues.push({
                        attr: { Name: attr, Id: 0, Category: "", DataType: "" },
                        value: t("propWinVaries"),
                        values: unique,
                    });
                } else {
                    finalValues.push({
                        attr: { Name: attr, Id: 0, Category: "", DataType: "" },
                        value: unique[0]?.value ?? "",
                        values: [],
                    });
                }
            }

            if (finalValues.length) {
                grouped.push({ category, values: finalValues });
            }
        }

        const order = [
            t("propWinIdentification"),
            t("propWinLocationCat"),
            t("propWinAttributes"),
            t("propWinMaterials"),
            t("propWinClassifications"),
            t("propWinPset"),
        ];

        grouped.sort((a, b) => {
            const aIndex = order.indexOf(a.category);
            const bIndex = order.indexOf(b.category);

            const aIsPset = a.category.includes("Pset");
            const bIsPset = b.category.includes("Pset");

            if ((aIndex === -1 && bIndex === -1) || (aIsPset && bIsPset)) {
                return a.category.localeCompare(b.category);
            }

            if (aIndex === -1) return 1;
            if (bIndex === -1) return -1;

            return aIndex - bIndex;
        });

        return { properties: grouped, errorMessage: null };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEntities, viewer, treeViewCache, objects, search, t]);
};

export default useSelectionProperties;
